import { useEffect, useMemo, useState } from "react";

const SITE_NAME = "Witch's Talisman Workshop";
const STORAGE_KEY = "talisman-generator-recents-v1";
const ADSTERRA_BANNER_HOST = normalizeAdHost(import.meta.env.VITE_ADSTERRA_BANNER_HOST || "www.highperformanceformat.com");
const ADSTERRA_NATIVE_HOST = normalizeAdHost(import.meta.env.VITE_ADSTERRA_NATIVE_HOST || ADSTERRA_BANNER_HOST);
const SHOW_AD_PLACEHOLDERS = import.meta.env.DEV || import.meta.env.VITE_ADSTERRA_SHOW_PLACEHOLDERS === "true";
const ADSTERRA_UNITS = {
  banner300x250: adsterraBannerUnit("banner300x250", "Banner 300x250", 300, 250, import.meta.env.VITE_ADSTERRA_300X250_KEY),
  banner728x90: adsterraBannerUnit("banner728x90", "Banner 728x90", 728, 90, import.meta.env.VITE_ADSTERRA_728X90_KEY),
  banner320x50: adsterraBannerUnit("banner320x50", "Banner 320x50", 320, 50, import.meta.env.VITE_ADSTERRA_320X50_KEY),
  banner468x60: adsterraBannerUnit("banner468x60", "Banner 468x60", 468, 60, import.meta.env.VITE_ADSTERRA_468X60_KEY),
  banner160x300: adsterraBannerUnit("banner160x300", "Banner 160x300", 160, 300, import.meta.env.VITE_ADSTERRA_160X300_KEY),
  banner160x600: adsterraBannerUnit("banner160x600", "Banner 160x600", 160, 600, import.meta.env.VITE_ADSTERRA_160X600_KEY),
  nativeBanner: {
    id: "nativeBanner",
    label: "Native Banner",
    kind: "native",
    width: 728,
    height: 180,
    key: cleanEnv(import.meta.env.VITE_ADSTERRA_NATIVE_KEY),
    containerId: cleanEnv(import.meta.env.VITE_ADSTERRA_NATIVE_CONTAINER),
    scriptSrc: cleanEnv(import.meta.env.VITE_ADSTERRA_NATIVE_SRC)
  }
};

const INTENTS = [
  { id: "wish", label: "Wish", verb: "binds a wish", sigil: "AUREA" },
  { id: "guard", label: "Guard", verb: "holds a boundary", sigil: "CUSTOS" },
  { id: "luck", label: "Luck", verb: "invites a turn", sigil: "FORTUNA" },
  { id: "love", label: "Love", verb: "keeps a tender vow", sigil: "AMARE" },
  { id: "focus", label: "Focus", verb: "sharpens an aim", sigil: "LUMEN" },
  { id: "release", label: "Release", verb: "lets a weight pass", sigil: "SOLVE" },
  { id: "curse", label: "Curse", verb: "seals a fictional curse", sigil: "UMBRA" }
];

const PALETTES = [
  {
    id: "sun-ink",
    label: "Sun Ink",
    paper: "#f8dfba",
    ink: "#24131f",
    accent: "#c33d4e",
    accent2: "#d29b31",
    glow: "#fff0af",
    night: "#171116"
  },
  {
    id: "jade-fire",
    label: "Jade Fire",
    paper: "#dff0d8",
    ink: "#10251f",
    accent: "#d45f32",
    accent2: "#2d8a7a",
    glow: "#f7e7a4",
    night: "#0d1918"
  },
  {
    id: "blood-moon",
    label: "Blood Moon",
    paper: "#f3d3cb",
    ink: "#231018",
    accent: "#9f1f35",
    accent2: "#d7a542",
    glow: "#ffd1bb",
    night: "#160d12"
  },
  {
    id: "blue-ember",
    label: "Blue Ember",
    paper: "#dce9f1",
    ink: "#0f1a29",
    accent: "#2a73a6",
    accent2: "#c88935",
    glow: "#cdefff",
    night: "#0b121f"
  },
  {
    id: "violet-gold",
    label: "Violet Gold",
    paper: "#eadcf3",
    ink: "#20152d",
    accent: "#733d9b",
    accent2: "#d0a33e",
    glow: "#f6ddff",
    night: "#130e1a"
  }
];

const STYLES = [
  { id: "celestial", label: "Celestial", glyphs: "✦✧✶✷✹✺✴✵" },
  { id: "thorn", label: "Thorn", glyphs: "⌁⌬⌭⌯⌰⌱⌲⌳" },
  { id: "oracle", label: "Oracle", glyphs: "ᚠᚢᚦᚨᚱᚲᚷᚹ" },
  { id: "lotus", label: "Lotus", glyphs: "◈◇◆◍◎◌◐◑" },
  { id: "storm", label: "Storm", glyphs: "△▽◇◊✕✚✜✣" }
];

const FRAME_TYPES = ["circle", "diamond", "arch", "tablet", "triad", "gate"];
const MOTIF_TYPES = ["constellation", "watching-eye", "crescent", "branching-tree", "compass", "lattice", "flame"];
const INTENT_DESIGNS = {
  wish: { motif: "constellation", frames: ["circle", "arch", "diamond"] },
  guard: { motif: "watching-eye", frames: ["gate", "tablet", "circle"] },
  luck: { motif: "compass", frames: ["diamond", "circle", "triad"] },
  love: { motif: "branching-tree", frames: ["arch", "tablet", "circle"] },
  focus: { motif: "lattice", frames: ["tablet", "diamond", "gate"] },
  release: { motif: "crescent", frames: ["triad", "circle", "arch"] },
  curse: { motif: "flame", frames: ["gate", "triad", "diamond"] }
};
const FORGE_DURATION_MS = 6800;

const FORGE_STEPS = [
  "mixing ink from unicorn tears",
  "writing the seal with a phoenix feather",
  "warming the parchment over moonfire",
  "binding the charm with silver thread",
  "sealing the talisman under starlight"
];

function getPathname() {
  return window.location.pathname.replace(/\/+$/, "") || "/";
}

function usePathname() {
  const [pathname, setPathname] = useState(getPathname);

  useEffect(() => {
    const onPop = () => setPathname(getPathname());
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  return pathname;
}

function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => {
    if (!window.matchMedia) return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mediaQuery = window.matchMedia(query);
    const update = () => setMatches(mediaQuery.matches);
    update();
    mediaQuery.addEventListener("change", update);
    return () => mediaQuery.removeEventListener("change", update);
  }, [query]);

  return matches;
}

function usePageMeta(title, description) {
  useEffect(() => {
    document.title = title === SITE_NAME ? title : `${title} - ${SITE_NAME}`;

    let descriptionMeta = document.querySelector('meta[name="description"]');
    if (!descriptionMeta) {
      descriptionMeta = document.createElement("meta");
      descriptionMeta.setAttribute("name", "description");
      document.head.appendChild(descriptionMeta);
    }
    descriptionMeta.setAttribute("content", description);

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", `${window.location.origin}${getPathname()}`);
  }, [description, title]);
}

function App() {
  const pathname = usePathname();
  const route = resolveRoute(pathname);
  const isSharedTalismanRoute = route === "shared-talisman" || (route === "home" && hasTalismanToken());

  if (isSharedTalismanRoute) {
    return <SharedTalismanPage />;
  }

  return (
    <>
      <GlobalRailAds />
      <div className="app-shell">
        <header className="site-header">
          <a className="brand" href="/" aria-label="Witch's Talisman Workshop home">
            <span className="brand-mark">WT</span>
            <span>Witch's Talisman Workshop</span>
          </a>
          <nav className="header-nav" aria-label="Primary">
            <a className={pathname === "/" ? "active" : ""} href="/">Generator</a>
            <a className={pathname === "/gallery" ? "active" : ""} href="/gallery">Gallery</a>
            <a className={pathname === "/about" ? "active" : ""} href="/about">About</a>
            <a className={pathname === "/privacy" ? "active" : ""} href="/privacy">Privacy</a>
            <a className={pathname === "/contact" ? "active" : ""} href="/contact">Contact</a>
          </nav>
        </header>

        <ResponsiveAdsterraSlot
          className="adsterra-site-top"
          desktopUnit={ADSTERRA_UNITS.banner728x90}
          mobileUnit={ADSTERRA_UNITS.banner320x50}
          placement="site top"
        />

        <main>
          {route === "home" ? <GeneratorPage /> : null}
          {route === "gallery" ? <GalleryPage /> : null}
          {route === "about" ? <AboutPage /> : null}
          {route === "privacy" ? <PrivacyPage /> : null}
          {route === "contact" ? <ContactPage /> : null}
          {route === "not-found" ? <NotFoundPage /> : null}
        </main>

        <SiteLinks />
      </div>
    </>
  );
}

function resolveRoute(pathname) {
  if (pathname === "/") return "home";
  if (pathname === "/talisman") return "shared-talisman";
  if (pathname === "/gallery") return "gallery";
  if (pathname === "/about") return "about";
  if (pathname === "/privacy") return "privacy";
  if (pathname === "/contact") return "contact";
  return "not-found";
}

function GeneratorPage() {
  const initialText = "May the path open at the right hour";
  const initialIntent = "wish";
  const initialTalisman = useMemo(() => createTalisman({
    text: initialText,
    intent: initialIntent,
    ...randomTalismanOptions()
  }), []);
  const [text, setText] = useState(initialText);
  const [intent, setIntent] = useState(initialIntent);
  const [talisman, setTalisman] = useState(initialTalisman);
  const [forge, setForge] = useState({ active: false, progress: 0, step: FORGE_STEPS[0] });
  const [notice, setNotice] = useState("");

  usePageMeta(
    SITE_NAME,
    "Create ornate talisman art from a wish, vow, blessing, or fictional curse."
  );

  function forgeTalisman(event) {
    event.preventDefault();
    const cleanText = text.trim();
    if (!cleanText) {
      setNotice("Write a wish, vow, blessing, or fictional curse first.");
      return;
    }

    const next = createTalisman({
      text: cleanText.slice(0, 260),
      intent,
      ...randomTalismanOptions()
    });

    setNotice("");
    setForge({ active: true, progress: 4, step: FORGE_STEPS[0] });
    const startTime = window.performance.now();
    const timer = window.setInterval(() => {
      const elapsed = window.performance.now() - startTime;
      const ratio = Math.min(elapsed / FORGE_DURATION_MS, 1);
      const progress = Math.min(4 + Math.pow(ratio, 0.82) * 92, 96);
      const stepIndex = Math.min(Math.floor(ratio * FORGE_STEPS.length), FORGE_STEPS.length - 1);
      setForge({ active: true, progress, step: FORGE_STEPS[stepIndex] });
    }, 180);

    window.setTimeout(() => {
      window.clearInterval(timer);
      setTalisman(next);
      saveRecent(next);
      setForge({ active: false, progress: 100, step: FORGE_STEPS[FORGE_STEPS.length - 1] });
    }, FORGE_DURATION_MS);
  }

  return (
    <>
      <section className="generator-stage" aria-labelledby="page-title">
        <form className="control-panel" onSubmit={forgeTalisman}>
          <div className="panel-heading">
            <p className="eyebrow">Ornate sigil art</p>
            <h1 id="page-title">Write the words. Forge the talisman.</h1>
          </div>

          <label className="wish-input">
            <span>Inscription</span>
            <textarea
              value={text}
              onChange={(event) => setText(event.target.value)}
              maxLength={260}
              rows={5}
              placeholder="Type a wish, a vow, a blessing, or a fictional curse..."
            />
            <small>{text.length}/260</small>
          </label>

          <div className="segmented" aria-label="Intent">
            {INTENTS.map((item) => (
              <button
                className={intent === item.id ? "active" : ""}
                key={item.id}
                type="button"
                onClick={() => setIntent(item.id)}
              >
                {item.label}
              </button>
            ))}
          </div>

          <button className="primary-action" type="submit" disabled={forge.active}>
            {forge.active ? "Forging..." : "Forge talisman"}
          </button>
          {notice ? <p className="notice">{notice}</p> : null}
        </form>

        <section className="result-panel" aria-label="Generated talisman">
          <TalismanPreview talisman={talisman} active={forge.active} forge={forge} />
          <ActionBar talisman={talisman} />
        </section>
      </section>

      <section className="adsterra-cluster home-ad-cluster" aria-label="Sponsored placements">
        <AdsterraSlot unit={ADSTERRA_UNITS.banner300x250} placement="home rectangle" />
        <AdsterraSlot className="adsterra-native-wide" unit={ADSTERRA_UNITS.nativeBanner} placement="home native" />
      </section>

      <ResponsiveAdsterraSlot
        className="adsterra-page-break"
        desktopUnit={ADSTERRA_UNITS.banner468x60}
        mobileUnit={ADSTERRA_UNITS.banner320x50}
        placement="home lower"
      />
    </>
  );
}

function SharedTalismanPage() {
  const talisman = useMemo(() => readTalismanFromUrl(), []);

  usePageMeta(
    "Shared Talisman",
    "View a sealed decorative talisman shared from Witch's Talisman Workshop."
  );

  useEffect(() => {
    if (talisman) {
      saveRecent(talisman);
    }
  }, [talisman]);

  if (!talisman) {
    return (
      <main className="shared-talisman-page shared-talisman-empty">
        <section className="empty-state">
          <h1>The talisman link has faded.</h1>
          <a className="primary-link" href="/">Forge a new one</a>
        </section>
      </main>
    );
  }

  return (
    <main className="shared-talisman-page" aria-label="Shared talisman">
      <ResponsiveAdsterraSlot
        className="shared-ad shared-ad-top"
        desktopUnit={ADSTERRA_UNITS.banner728x90}
        mobileUnit={ADSTERRA_UNITS.banner320x50}
        placement="shared top"
      />

      <section className="shared-talisman-viewer">
        <div className="shared-talisman-heading">
          <span aria-hidden="true" className="shared-sigil">✦</span>
          <p>Click the talisman to reveal the message</p>
          <span aria-hidden="true" className="shared-sigil">✦</span>
        </div>

        <TalismanPreview talisman={talisman} />

        <div className="shared-talisman-actions">
          <a className="primary-link shared-create-link" href="/">Create a talisman for someone else</a>
        </div>
      </section>

      <section className="shared-ad-stack" aria-label="Sponsored placements">
        <AdsterraSlot unit={ADSTERRA_UNITS.banner300x250} placement="shared rectangle" />
        <ResponsiveAdsterraSlot
          desktopUnit={ADSTERRA_UNITS.banner468x60}
          mobileUnit={ADSTERRA_UNITS.banner320x50}
          placement="shared lower"
        />
      </section>
    </main>
  );
}

function TalismanPreview({ talisman, active = false, forge = { progress: 0, step: "" } }) {
  const [revealed, setRevealed] = useState(false);
  const design = useMemo(() => makeDesign(talisman), [talisman.id]);
  const colors = getPalette(talisman.palette);
  const intent = getIntent(talisman.intent);
  const style = getStyle(talisman.style);
  const revealLines = wrapText(talisman.text, 30, 6);

  useEffect(() => {
    setRevealed(false);
  }, [talisman.id]);

  return (
    <div className={`talisman-frame ${active ? "is-forging" : ""} ${revealed ? "is-revealed" : ""}`}>
      <button
        className="talisman-button"
        type="button"
        onClick={() => setRevealed((current) => !current)}
        disabled={active}
        aria-expanded={revealed}
        aria-label={revealed ? "Hide talisman inscription" : "Reveal talisman inscription"}
      >
      <svg
        id="talisman-art"
        className="talisman-svg"
        viewBox="0 0 900 1400"
        xmlns="http://www.w3.org/2000/svg"
        role="img"
        aria-label="Sealed decorative talisman"
      >
        <defs>
          <radialGradient id={`halo-${talisman.id}`} cx="50%" cy="34%" r="62%">
            <stop offset="0" stopColor={colors.glow} />
            <stop offset="0.42" stopColor={colors.accent} stopOpacity="0.72" />
            <stop offset="1" stopColor={colors.night} />
          </radialGradient>
          <linearGradient id={`paper-${talisman.id}`} x1="0" x2="1" y1="0" y2="1">
            <stop offset="0" stopColor={colors.paper} />
            <stop offset="0.55" stopColor="#fff4da" />
            <stop offset="1" stopColor={colors.paper} />
          </linearGradient>
          <filter id={`rough-${talisman.id}`}>
            <feTurbulence type="fractalNoise" baseFrequency="0.85" numOctaves="2" seed={design.seed % 97} />
            <feColorMatrix type="saturate" values="0" />
            <feComponentTransfer>
              <feFuncA type="table" tableValues="0 0.16" />
            </feComponentTransfer>
          </filter>
        </defs>

        <rect width="900" height="1400" fill={colors.night} />
        <circle cx="450" cy="420" r="390" fill={`url(#halo-${talisman.id})`} opacity="0.54" />
        <rect x="100" y="58" width="700" height="1284" rx="26" fill={`url(#paper-${talisman.id})`} />
        <rect x="100" y="58" width="700" height="1284" rx="26" fill="#000" filter={`url(#rough-${talisman.id})`} />
        <rect x="128" y="92" width="644" height="1216" rx="18" fill="none" stroke={colors.ink} strokeWidth="7" />
        <rect x="150" y="116" width="600" height="1168" rx="14" fill="none" stroke={colors.accent2} strokeWidth="4" strokeDasharray="18 14" opacity="0.9" />

        {design.stars.map((star) => (
          <circle key={star.id} cx={star.x} cy={star.y} r={star.r} fill={star.color} opacity={star.opacity} />
        ))}

        {design.rays.map((ray) => (
          <path key={ray.id} d={ray.d} fill="none" stroke={colors.accent2} strokeWidth={ray.width} opacity={ray.opacity} />
        ))}

        <CenterSigil design={design} colors={colors} intent={intent} style={style} />

        <g>
          <path d="M186 864 C268 820 632 820 714 864 L676 1014 C592 1054 308 1054 224 1014 Z" fill="#fff6dc" opacity="0.88" stroke={colors.ink} strokeWidth="5" />
          <path d="M300 918 H600 M334 962 H566" stroke={colors.ink} strokeWidth="8" strokeLinecap="round" opacity="0.68" />
          <circle cx="450" cy="944" r="34" fill="none" stroke={colors.accent} strokeWidth="7" />
          <path d="M450 900 462 934 498 944 462 954 450 988 438 954 402 944 438 934Z" fill="none" stroke={colors.accent2} strokeWidth="6" strokeLinejoin="round" />
          <text x="450" y="1038" textAnchor="middle" fill={colors.ink} fontFamily="Georgia, serif" fontSize="24" fontWeight="800" opacity="0.72">
            SEALED INSCRIPTION
          </text>
        </g>

        <text x="450" y="1124" textAnchor="middle" fill={colors.accent} fontFamily="Georgia, serif" fontSize="34" fontWeight="800">
          {intent.verb}
        </text>
        <text x="450" y="1182" textAnchor="middle" fill={colors.ink} fontFamily="Georgia, serif" fontSize="22">
          seal {talisman.id.toUpperCase()}
        </text>
        <path d="M250 1236 H650 M284 1264 H616" stroke={colors.accent2} strokeWidth="6" strokeLinecap="round" />
      </svg>
      </button>

      {active ? (
        <div className="forge-overlay" aria-live="polite">
          <div className="forge-ring">
            <span>{Math.round(forge.progress)}%</span>
          </div>
          <strong>{forge.step}</strong>
          <div className="forge-bar"><span style={{ width: `${forge.progress}%` }} /></div>
        </div>
      ) : null}

      {revealed && !active ? (
        <div className="reveal-panel" role="dialog" aria-label="Revealed talisman inscription">
          <p className="eyebrow">Revealed inscription</p>
          <div className="revealed-text">
            {revealLines.map((line, index) => (
              <span key={`${line}-${index}`}>{line}</span>
            ))}
          </div>
          <button type="button" onClick={() => setRevealed(false)}>Close seal</button>
        </div>
      ) : null}
    </div>
  );
}

function CenterSigil({ design, colors, intent, style }) {
  return (
    <g transform="translate(450 518)">
      <CenterFrame type={design.frameType} colors={colors} />
      <Motif motif={design.motif} colors={colors} />

      {design.nodes.map((node) => (
        <circle key={node.id} cx={node.x} cy={node.y} r={node.r} fill={node.fill} stroke={colors.ink} strokeWidth={node.strokeWidth} opacity={node.opacity} />
      ))}

      {design.glyphs.map((glyph) => (
        <text
          key={glyph.id}
          x={glyph.x}
          y={glyph.y}
          transform={`rotate(${glyph.rotation} ${glyph.x} ${glyph.y})`}
          textAnchor="middle"
          dominantBaseline="middle"
          fill={glyph.fill}
          fontSize={glyph.size}
          fontFamily="Georgia, serif"
          fontWeight="700"
          opacity={glyph.opacity}
        >
          {glyph.char}
        </text>
      ))}

      <path d="M-172 276 C-86 252 86 252 172 276" fill="none" stroke={colors.accent2} strokeWidth="5" strokeLinecap="round" opacity="0.8" />
      <text textAnchor="middle" y="306" fill={colors.ink} fontFamily="Georgia, serif" fontSize="38" fontWeight="800">
        {intent.sigil}
      </text>
      <text textAnchor="middle" y="344" fill={colors.accent} fontFamily="Georgia, serif" fontSize="22" fontWeight="700">
        {style.label.toUpperCase()}
      </text>
    </g>
  );
}

function CenterFrame({ type, colors }) {
  if (type === "diamond") {
    return (
      <>
        <path d="M0 -282 L250 0 0 282 -250 0 Z" fill="none" stroke={colors.ink} strokeWidth="9" strokeLinejoin="round" />
        <path d="M0 -226 L198 0 0 226 -198 0 Z" fill="none" stroke={colors.accent} strokeWidth="5" strokeDasharray="16 14" />
        <path d="M0 -158 L134 0 0 158 -134 0 Z" fill="none" stroke={colors.accent2} strokeWidth="6" />
      </>
    );
  }

  if (type === "arch") {
    return (
      <>
        <path d="M-226 236 V-62 C-226 -214 226 -214 226 -62 V236 Z" fill="none" stroke={colors.ink} strokeWidth="9" strokeLinejoin="round" />
        <path d="M-184 206 V-52 C-184 -164 184 -164 184 -52 V206 Z" fill="none" stroke={colors.accent} strokeWidth="5" strokeDasharray="14 16" />
        <path d="M-118 176 V-24 C-118 -96 118 -96 118 -24 V176" fill="none" stroke={colors.accent2} strokeWidth="6" strokeLinecap="round" />
      </>
    );
  }

  if (type === "tablet") {
    return (
      <>
        <rect x="-228" y="-248" width="456" height="496" rx="34" fill="none" stroke={colors.ink} strokeWidth="9" />
        <rect x="-188" y="-208" width="376" height="416" rx="20" fill="none" stroke={colors.accent} strokeWidth="5" strokeDasharray="20 14" />
        <path d="M-150 -156 H150 M-150 156 H150 M-188 -68 H188 M-188 68 H188" fill="none" stroke={colors.accent2} strokeWidth="5" strokeLinecap="round" opacity="0.72" />
      </>
    );
  }

  if (type === "triad") {
    return (
      <>
        <path d="M0 -274 L238 150 H-238 Z" fill="none" stroke={colors.ink} strokeWidth="9" strokeLinejoin="round" />
        <path d="M0 274 L238 -150 H-238 Z" fill="none" stroke={colors.accent} strokeWidth="5" strokeDasharray="18 16" strokeLinejoin="round" />
        <circle r="154" fill="none" stroke={colors.accent2} strokeWidth="6" />
      </>
    );
  }

  if (type === "gate") {
    return (
      <>
        <path d="M-252 -232 H252 M-216 -232 V238 M216 -232 V238 M-252 238 H252" fill="none" stroke={colors.ink} strokeWidth="9" strokeLinecap="round" />
        <path d="M-164 -178 H164 V178 H-164 Z" fill="none" stroke={colors.accent} strokeWidth="5" strokeDasharray="16 14" />
        <circle cx="-216" cy="-232" r="24" fill={colors.paper} stroke={colors.accent2} strokeWidth="6" />
        <circle cx="216" cy="-232" r="24" fill={colors.paper} stroke={colors.accent2} strokeWidth="6" />
      </>
    );
  }

  return (
    <>
      <circle r="258" fill="none" stroke={colors.ink} strokeWidth="8" />
      <circle r="212" fill="none" stroke={colors.accent} strokeWidth="5" strokeDasharray="18 18" />
      <circle r="138" fill="none" stroke={colors.accent2} strokeWidth="6" />
    </>
  );
}

function Motif({ motif, colors }) {
  if (motif.type === "watching-eye") {
    return (
      <g>
        {motif.lashes.map((lash) => (
          <path key={lash.id} d={lash.d} fill="none" stroke={colors.accent2} strokeWidth={lash.width} strokeLinecap="round" opacity="0.78" />
        ))}
        <path d={motif.eyePath} fill="none" stroke={colors.ink} strokeWidth="11" strokeLinejoin="round" />
        <ellipse cx="0" cy="0" rx={motif.irisX} ry={motif.irisY} fill="none" stroke={colors.accent} strokeWidth="8" />
        <circle cx="0" cy="0" r={motif.pupil} fill={colors.ink} />
        <circle cx="-16" cy="-18" r="8" fill={colors.glow} opacity="0.9" />
        {motif.orbits.map((dot) => (
          <circle key={dot.id} cx={dot.x} cy={dot.y} r={dot.r} fill={dot.fill} opacity={dot.opacity} />
        ))}
      </g>
    );
  }

  if (motif.type === "crescent") {
    return (
      <g>
        <circle cx={motif.cx} cy={motif.cy} r={motif.radius} fill={colors.accent} opacity="0.92" />
        <circle cx={motif.cutX} cy={motif.cy - 8} r={motif.cutRadius} fill={colors.paper} opacity="0.96" />
        <path d={motif.orbitPath} fill="none" stroke={colors.ink} strokeWidth="7" strokeLinecap="round" />
        {motif.sparks.map((spark) => (
          <path key={spark.id} d={spark.d} fill="none" stroke={spark.fill} strokeWidth={spark.width} strokeLinecap="round" strokeLinejoin="round" opacity={spark.opacity} />
        ))}
      </g>
    );
  }

  if (motif.type === "branching-tree") {
    return (
      <g>
        {motif.roots.map((root) => (
          <path key={root.id} d={root.d} fill="none" stroke={colors.accent2} strokeWidth={root.width} strokeLinecap="round" opacity="0.78" />
        ))}
        {motif.branches.map((branch) => (
          <path key={branch.id} d={branch.d} fill="none" stroke={branch.color} strokeWidth={branch.width} strokeLinecap="round" />
        ))}
        {motif.leaves.map((leaf) => (
          <ellipse key={leaf.id} cx={leaf.x} cy={leaf.y} rx={leaf.rx} ry={leaf.ry} transform={`rotate(${leaf.rotation} ${leaf.x} ${leaf.y})`} fill={leaf.fill} opacity={leaf.opacity} />
        ))}
      </g>
    );
  }

  if (motif.type === "compass") {
    return (
      <g>
        {motif.spikes.map((spike) => (
          <path key={spike.id} d={spike.d} fill={spike.fill} stroke={colors.ink} strokeWidth="4" strokeLinejoin="round" opacity={spike.opacity} />
        ))}
        <circle r={motif.outer} fill="none" stroke={colors.accent} strokeWidth="7" />
        <circle r={motif.inner} fill="none" stroke={colors.ink} strokeWidth="5" strokeDasharray="10 12" />
        <path d={motif.crossPath} fill="none" stroke={colors.accent2} strokeWidth="6" strokeLinecap="round" />
      </g>
    );
  }

  if (motif.type === "lattice") {
    return (
      <g>
        {motif.paths.map((path) => (
          <path key={path.id} d={path.d} fill="none" stroke={path.stroke} strokeWidth={path.width} strokeLinecap="round" opacity={path.opacity} />
        ))}
        {motif.cells.map((cell) => (
          <path key={cell.id} d={cell.d} fill="none" stroke={colors.ink} strokeWidth="4" strokeLinejoin="round" opacity={cell.opacity} />
        ))}
      </g>
    );
  }

  if (motif.type === "flame") {
    return (
      <g>
        {motif.flames.map((flame) => (
          <path key={flame.id} d={flame.d} fill={flame.fill} stroke={colors.ink} strokeWidth={flame.width} strokeLinejoin="round" opacity={flame.opacity} />
        ))}
        {motif.smoke.map((line) => (
          <path key={line.id} d={line.d} fill="none" stroke={line.stroke} strokeWidth={line.width} strokeLinecap="round" opacity={line.opacity} />
        ))}
      </g>
    );
  }

  return (
    <g>
      <path d={motif.primaryPath} fill="none" stroke={colors.ink} strokeWidth="11" strokeLinejoin="round" />
      <path d={motif.secondaryPath} fill="none" stroke={colors.accent} strokeWidth="7" strokeLinecap="round" />
      {motif.segments.map((segment) => (
        <path key={segment.id} d={segment.d} fill="none" stroke={segment.stroke} strokeWidth={segment.width} strokeLinecap="round" opacity={segment.opacity} />
      ))}
    </g>
  );
}

function ActionBar({ talisman }) {
  const [copied, setCopied] = useState(false);

  async function copyLink() {
    const url = shareUrlFor(talisman).toString();
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      setCopied(false);
    }
  }

  async function share() {
    const url = shareUrlFor(talisman).toString();
    if (navigator.share) {
      try {
        await navigator.share({
          title: SITE_NAME,
          text: "A sealed talisman is waiting to be opened.",
          url
        });
        return;
      } catch {
        return;
      }
    }
    copyLink();
  }

  return (
    <div className="action-bar">
      <button type="button" onClick={() => downloadSvg(talisman)}>Download SVG</button>
      <button type="button" onClick={() => downloadPng(talisman)}>Download PNG</button>
      <button type="button" onClick={copyLink}>{copied ? "Copied" : "Copy link"}</button>
      <button type="button" onClick={share}>Share</button>
    </div>
  );
}

function GalleryPage() {
  const [items, setItems] = useState(() => getRecents());

  usePageMeta(
    "Gallery",
    "Open talismans created on this device, copy links, or clear saved items."
  );

  function clear() {
    localStorage.removeItem(STORAGE_KEY);
    setItems([]);
  }

  return (
    <>
      <PageHeading eyebrow="Gallery" title="Saved talismans" description="Saved on this device." />
      <section className="content-section gallery-section">
        <div className="section-actions">
          <span>{items.length} saved</span>
          <button type="button" onClick={clear} disabled={!items.length}>Clear gallery</button>
        </div>
        {items.length ? (
          <div className="gallery-grid">
            {items.map((item) => (
              <a className="gallery-card" href={shareUrlFor(item).toString().replace(window.location.origin, "")} key={item.id}>
                <TalismanMini talisman={item} />
                <strong>Sealed {getIntent(item.intent).label} talisman</strong>
                <span>{getIntent(item.intent).label} · {getPalette(item.palette).label}</span>
              </a>
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <h2>No talismans saved yet.</h2>
            <a className="primary-link" href="/">Forge the first one</a>
          </div>
        )}
      </section>
      <AdsterraSlot className="adsterra-native-wide" unit={ADSTERRA_UNITS.nativeBanner} placement="gallery native" />
      <section className="adsterra-cluster gallery-ad-cluster" aria-label="Sponsored placements">
        <AdsterraSlot unit={ADSTERRA_UNITS.banner300x250} placement="gallery rectangle" />
        <ResponsiveAdsterraSlot
          desktopUnit={ADSTERRA_UNITS.banner468x60}
          mobileUnit={ADSTERRA_UNITS.banner320x50}
          placement="gallery lower"
        />
      </section>
    </>
  );
}

function TalismanMini({ talisman }) {
  const colors = getPalette(talisman.palette);
  const design = useMemo(() => makeDesign(talisman), [talisman.id]);
  return (
    <svg className="mini-svg" viewBox="0 0 220 320" aria-hidden="true">
      <rect width="220" height="320" rx="12" fill={colors.paper} />
      <rect x="14" y="14" width="192" height="292" rx="8" fill="none" stroke={colors.ink} strokeWidth="4" />
      <g transform="translate(110 125) scale(0.26)">
        <CenterFrame type={design.frameType} colors={colors} />
        <Motif motif={design.motif} colors={colors} />
      </g>
      <text x="110" y="230" textAnchor="middle" fill={colors.ink} fontFamily="Georgia, serif" fontSize="18" fontWeight="800">
        {getIntent(talisman.intent).sigil}
      </text>
    </svg>
  );
}

function AboutPage() {
  usePageMeta(
    "About",
    "About Witch's Talisman Workshop, a browser-based decorative sigil and talisman art tool."
  );
  return (
    <InfoPage eyebrow="About" title="Witch's Talisman Workshop">
      <p>
        Witch's Talisman Workshop turns user-written words into ornate decorative sigil art. It is made for wishes, vows,
        blessings, creative prompts, fictional curses, character props, journaling, and visual rituals.
      </p>
      <p>
        The generator runs in the browser. It uses deterministic design seeds so a shared link can recreate the same
        talisman without an account or backend database.
      </p>
      <p>
        Results are entertainment and art. They are not a substitute for legal, medical, safety, or professional advice,
        and they should not be used to threaten or harass real people.
      </p>
    </InfoPage>
  );
}

function PrivacyPage() {
  usePageMeta(
    "Privacy Policy",
    "Privacy Policy for Witch's Talisman Workshop, including browser storage, share links, advertising partners, and hosting data."
  );
  return (
    <InfoPage eyebrow="Privacy" title="Privacy Policy">
      <p className="updated">Last updated: June 13, 2026</p>
      <h2>Information you enter</h2>
      <p>
        Talisman inscriptions are processed in your browser. The site does not require an account and does not store
        your generated text in a site database.
      </p>
      <h2>Share links</h2>
      <p>
        When you copy or share a talisman link, the inscription and design settings are encoded in the URL. Anyone with
        that link can view the same talisman.
      </p>
      <h2>Local storage</h2>
      <p>
        Saved talismans are stored in this browser's local storage on this device. You can clear them from the Gallery page.
      </p>
      <h2>Advertising and cookies</h2>
      <p>
        The site may show ads from Adsterra. Advertising partners may use cookies, browser identifiers, IP address,
        device information, and page interaction data to deliver ads, measure performance, prevent fraud, and improve
        relevance.
      </p>
      <p>
        Saved talismans use this browser's local storage. You can clear saved talismans from the Gallery page or clear
        site data in your browser settings.
      </p>
      <h2>Hosting and technical data</h2>
      <p>
        The site is hosted on Cloudflare Pages. Hosting providers may process technical information such as IP address,
        browser type, requested URL, timestamps, and security logs to deliver the site and maintain reliability.
      </p>
      <h2>Contact</h2>
      <p>
        For privacy questions, use the <a href="/contact">Contact page</a>.
      </p>
    </InfoPage>
  );
}

function ContactPage() {
  usePageMeta(
    "Contact",
    "Contact Witch's Talisman Workshop for site feedback, privacy questions, policy issues, and correction requests."
  );
  return (
    <InfoPage eyebrow="Contact" title="Contact">
      <p>
        Use this page for site feedback, privacy questions, policy concerns, or reports about abusive shared content.
      </p>
      <h2>Site owner</h2>
      <p>
        Witch's Talisman Workshop is maintained by the site owner under the GitHub profile{" "}
        <a href="https://github.com/wonyeong0810" rel="noopener noreferrer">wonyeong0810</a>.
      </p>
      <h2>Helpful details</h2>
      <ul>
        <li>The page URL or shared talisman link.</li>
        <li>The browser and device where the issue happened.</li>
        <li>A short description of the correction, privacy question, or policy concern.</li>
      </ul>
    </InfoPage>
  );
}

function NotFoundPage() {
  usePageMeta(
    "Page Not Found",
    "The requested Witch's Talisman Workshop page was not found."
  );
  return (
    <section className="content-section empty-state">
      <p className="eyebrow">404</p>
      <h1>Page not found</h1>
      <a className="primary-link" href="/">Open generator</a>
    </section>
  );
}

function PageHeading({ eyebrow, title, description }) {
  return (
    <section className="page-heading">
      <p className="eyebrow">{eyebrow}</p>
      <h1>{title}</h1>
      <p>{description}</p>
    </section>
  );
}

function InfoPage({ eyebrow, title, children }) {
  return (
    <section className="info-layout">
      <div className="info-card">
        <p className="eyebrow">{eyebrow}</p>
        <h1>{title}</h1>
        <div className="prose">{children}</div>
      </div>
      <aside className="info-ad-stack" aria-label="Sponsored placements">
        <AdsterraSlot unit={ADSTERRA_UNITS.banner160x300} placement="info side tall" />
        <AdsterraSlot unit={ADSTERRA_UNITS.banner300x250} placement="info side rectangle" />
      </aside>
    </section>
  );
}

function SiteLinks() {
  return (
    <footer className="site-footer">
      <ResponsiveAdsterraSlot
        className="adsterra-footer"
        desktopUnit={ADSTERRA_UNITS.banner468x60}
        mobileUnit={ADSTERRA_UNITS.banner320x50}
        placement="site footer"
      />
      <nav className="policy-links" aria-label="Site information">
        <a href="/about">About</a>
        <a href="/contact">Contact</a>
        <a href="/privacy">Privacy Policy</a>
      </nav>
    </footer>
  );
}

function GlobalRailAds() {
  const showRails = useMediaQuery("(min-width: 1520px)");

  if (!showRails) {
    return null;
  }

  return (
    <>
      <AdsterraSlot className="adsterra-rail adsterra-rail-left" unit={ADSTERRA_UNITS.banner160x300} placement="left rail" />
      <AdsterraSlot className="adsterra-rail adsterra-rail-right" unit={ADSTERRA_UNITS.banner160x600} placement="right rail" />
    </>
  );
}

function ResponsiveAdsterraSlot({ desktopUnit, mobileUnit, className = "", placement = "" }) {
  const isCompact = useMediaQuery("(max-width: 760px)");
  return <AdsterraSlot className={className} unit={isCompact ? mobileUnit : desktopUnit} placement={placement} />;
}

function AdsterraSlot({ unit, className = "", placement = "" }) {
  if (!unit) return null;

  const hasScript = Boolean(unit.scriptSrc || unit.key);
  if (!hasScript && !SHOW_AD_PLACEHOLDERS) {
    return null;
  }

  const frameHtml = hasScript ? createAdsterraFrameHtml(unit) : "";

  return (
    <aside
      className={`adsterra-slot adsterra-slot-${unit.id} ${className}`.trim()}
      style={{
        "--ad-width": `${unit.width}px`,
        "--ad-height": `${unit.height}px`
      }}
      aria-label="Advertisement"
      data-placement={placement}
    >
      {hasScript ? (
        <iframe
          className="adsterra-frame"
          title={`${unit.label} advertisement`}
          width={unit.width}
          height={unit.height}
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-forms allow-top-navigation-by-user-activation"
          srcDoc={frameHtml}
        />
      ) : (
        <div className="adsterra-placeholder">
          <span>Advertisement</span>
          <strong>{unit.label}</strong>
        </div>
      )}
    </aside>
  );
}

function cleanEnv(value) {
  return String(value || "").trim();
}

function normalizeAdHost(value) {
  return cleanEnv(value)
    .replace(/^https?:\/\//, "")
    .replace(/^\/\//, "")
    .replace(/\/+$/, "");
}

function adsterraBannerUnit(id, label, width, height, key) {
  return {
    id,
    label,
    kind: "banner",
    width,
    height,
    key: cleanEnv(key)
  };
}

function createAdsterraFrameHtml(unit) {
  const markup = unit.kind === "native" ? createNativeAdsterraMarkup(unit) : createBannerAdsterraMarkup(unit);

  return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <style>
      html,
      body {
        margin: 0;
        min-width: 0;
        min-height: 0;
        overflow: hidden;
        background: transparent;
      }

      body {
        display: flex;
        align-items: center;
        justify-content: center;
      }
    </style>
  </head>
  <body>${markup}</body>
</html>`;
}

function createBannerAdsterraMarkup(unit) {
  const scriptValue = unit.scriptSrc || unit.key;
  const key = extractAdsterraKey(unit.key || scriptValue);
  const src = resolveAdsterraScriptSrc(scriptValue, ADSTERRA_BANNER_HOST);

  return `<script>
    var atOptions = {
      'key': '${escapeScriptValue(key)}',
      'format': 'iframe',
      'height': ${Number(unit.height)},
      'width': ${Number(unit.width)},
      'params': {}
    };
  </script>
  <script src="${escapeHtmlAttribute(src)}"></script>`;
}

function createNativeAdsterraMarkup(unit) {
  const scriptValue = unit.scriptSrc || unit.key;
  const src = resolveAdsterraScriptSrc(scriptValue, ADSTERRA_NATIVE_HOST);
  const containerId = unit.containerId || `container-${extractAdsterraKey(scriptValue)}`;

  return `<div id="${escapeHtmlAttribute(containerId)}"></div>
  <script async="async" data-cfasync="false" src="${escapeHtmlAttribute(src)}"></script>`;
}

function resolveAdsterraScriptSrc(value, fallbackHost) {
  const cleanValue = cleanEnv(value);
  if (/^https?:\/\//.test(cleanValue)) return cleanValue;
  if (cleanValue.startsWith("//")) return `https:${cleanValue}`;
  return `https://${fallbackHost}/${cleanValue.replace(/^\/+|\/+$/g, "")}/invoke.js`;
}

function extractAdsterraKey(value) {
  const cleanValue = cleanEnv(value).split("?")[0].replace(/\/+$/, "");
  const parts = cleanValue.split("/").filter(Boolean);
  const invokeIndex = parts.findIndex((part) => part === "invoke.js");
  if (invokeIndex > 0) return parts[invokeIndex - 1];
  return parts[parts.length - 1] || "";
}

function escapeHtmlAttribute(value) {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function escapeScriptValue(value) {
  return String(value)
    .replace(/\\/g, "\\\\")
    .replace(/'/g, "\\'")
    .replace(/\r?\n/g, "");
}

function createTalisman({ text, intent, palette, style, density, seed }) {
  const cleanSeed = seed || "sealed";
  const source = `${text}|${intent}|${palette}|${style}|${density}|${cleanSeed}`;
  return {
    id: hashString(source).toString(36),
    text,
    intent,
    palette,
    style,
    density,
    seed: cleanSeed,
    createdAt: new Date().toISOString()
  };
}

function randomTalismanOptions() {
  const densitySteps = 19;
  return {
    palette: randomItem(PALETTES).id,
    style: randomItem(STYLES).id,
    density: 24 + randomInt(densitySteps) * 4,
    seed: randomSeed()
  };
}

function randomItem(items) {
  return items[randomInt(items.length)] || items[0];
}

function randomSeed() {
  const words = ["ember", "mirror", "orbit", "velvet", "thorn", "crown", "rune", "lantern", "mist", "cipher", "star", "amber"];
  return `${randomItem(words)}-${randomItem(words)}-${randomInt(9999).toString().padStart(4, "0")}`;
}

function randomInt(max) {
  if (window.crypto?.getRandomValues) {
    const data = new Uint32Array(1);
    window.crypto.getRandomValues(data);
    return data[0] % max;
  }
  return Math.floor(Math.random() * max);
}

function makeDesign(talisman) {
  const seed = hashString(`${talisman.id}|${talisman.text}|${talisman.seed}`);
  const rng = mulberry32(seed);
  const colors = getPalette(talisman.palette);
  const style = getStyle(talisman.style);
  const count = Math.max(24, Math.min(Number(talisman.density) || 64, 96));
  const glyphSource = [...style.glyphs];
  const intentDesign = getIntentDesign(talisman.intent);
  const frameType = chooseByRng(intentDesign.frames, rng);
  const motifType = intentDesign.motif;
  const glyphLayout = chooseByRng(["orbit", "inner-orbit", "columns"], rng);

  const stars = Array.from({ length: count }, (_, index) => ({
    id: `s-${index}`,
    x: 130 + rng() * 640,
    y: 84 + rng() * 1200,
    r: 1.3 + rng() * 3.4,
    color: rng() > 0.5 ? colors.accent2 : colors.accent,
    opacity: 0.14 + rng() * 0.34
  }));

  const glyphs = makeGlyphs(glyphLayout, rng, colors, glyphSource);
  const nodeCount = 6 + Math.floor(rng() * 8);
  const nodes = Array.from({ length: nodeCount }, (_, index) => {
    const angle = (Math.PI * 2 * index) / nodeCount - Math.PI / 2 + rng() * 0.18;
    const radius = 154 + rng() * 84;
    const [x, y] = polarPoint(angle, radius);
    return {
      id: `n-${index}`,
      x,
      y,
      r: 6 + rng() * 7,
      fill: index % 2 ? colors.paper : colors.accent2,
      strokeWidth: 2.6 + rng() * 1.4,
      opacity: 0.72 + rng() * 0.25
    };
  });

  const rays = Array.from({ length: 16 }, (_, index) => {
    const y = 166 + index * 69;
    const wobble = 20 + rng() * 60;
    return {
      id: `r-${index}`,
      d: `M${142 + rng() * 30} ${round(y)} C${260} ${round(y - wobble)} ${640} ${round(y + wobble)} ${756 - rng() * 28} ${round(y)}`,
      width: 1.2 + rng() * 2,
      opacity: 0.13 + rng() * 0.16
    };
  });

  return {
    seed,
    frameType,
    motif: makeMotif(motifType, rng, colors),
    stars,
    glyphs,
    nodes,
    rays
  };
}

function makeGlyphs(layout, rng, colors, glyphSource) {
  if (layout === "columns") {
    return Array.from({ length: 28 }, (_, index) => {
      const side = index % 2 ? -1 : 1;
      const row = Math.floor(index / 2);
      const x = side * (216 + rng() * 20);
      const y = -210 + row * 32 + rng() * 8;
      return {
        id: `g-${index}`,
        char: chooseByRng(glyphSource, rng) || "✦",
        x,
        y,
        rotation: side * 90,
        size: 24 + rng() * 8,
        fill: row % 3 === 0 ? colors.accent : colors.ink,
        opacity: 0.82
      };
    });
  }

  const total = layout === "inner-orbit" ? 26 : 40;
  const baseRadius = layout === "inner-orbit" ? 174 : 236;
  return Array.from({ length: total }, (_, index) => {
    const angle = (Math.PI * 2 * index) / total - Math.PI / 2;
    const radius = baseRadius + (index % 2) * (layout === "inner-orbit" ? 12 : 18);
    const [x, y] = polarPoint(angle, radius);
    return {
      id: `g-${index}`,
      char: chooseByRng(glyphSource, rng) || "✦",
      x,
      y,
      rotation: (angle * 180) / Math.PI + 90,
      size: 22 + rng() * 10,
      fill: index % 3 === 0 ? colors.accent : colors.ink,
      opacity: 0.76 + rng() * 0.2
    };
  });
}

function makeMotif(type, rng, colors) {
  if (type === "watching-eye") {
    const eyeHeight = 92 + rng() * 36;
    const eyeWidth = 164 + rng() * 42;
    const eyePath = `M-${round(eyeWidth)} 0 C-${round(eyeWidth * 0.48)} -${round(eyeHeight)} ${round(eyeWidth * 0.48)} -${round(eyeHeight)} ${round(eyeWidth)} 0 C${round(eyeWidth * 0.48)} ${round(eyeHeight)} -${round(eyeWidth * 0.48)} ${round(eyeHeight)} -${round(eyeWidth)} 0 Z`;
    const lashes = Array.from({ length: 14 }, (_, index) => {
      const top = index < 7;
      const slot = top ? index : index - 7;
      const x = -132 + slot * 44;
      const y = top ? -52 - rng() * 20 : 52 + rng() * 20;
      const tipY = top ? y - 42 - rng() * 34 : y + 42 + rng() * 34;
      return {
        id: `lash-${index}`,
        d: `M${round(x)} ${round(y)} C${round(x + (slot - 3) * 5)} ${round((y + tipY) / 2)} ${round(x + (slot - 3) * 12)} ${round(tipY)} ${round(x + (slot - 3) * 18)} ${round(tipY)}`,
        width: 3 + rng() * 3
      };
    });
    const orbits = Array.from({ length: 9 }, (_, index) => {
      const angle = (Math.PI * 2 * index) / 9 + rng() * 0.14;
      const [x, y] = polarPoint(angle, 142 + rng() * 28);
      return {
        id: `eye-dot-${index}`,
        x,
        y,
        r: 4 + rng() * 5,
        fill: index % 2 ? colors.accent2 : colors.accent,
        opacity: 0.78
      };
    });
    return { type, eyePath, lashes, orbits, irisX: 46 + rng() * 18, irisY: 62 + rng() * 14, pupil: 21 + rng() * 11 };
  }

  if (type === "crescent") {
    const sparks = Array.from({ length: 15 }, (_, index) => {
      const angle = (Math.PI * 2 * index) / 15 + rng() * 0.2;
      const radius = 72 + rng() * 120;
      const [x, y] = polarPoint(angle, radius);
      const size = 8 + rng() * 16;
      return {
        id: `spark-${index}`,
        d: `M${round(x)} ${round(y - size)} L${round(x)} ${round(y + size)} M${round(x - size)} ${round(y)} L${round(x + size)} ${round(y)}`,
        fill: index % 2 ? colors.accent2 : colors.ink,
        width: 2.5 + rng() * 3,
        opacity: 0.62 + rng() * 0.28
      };
    });
    return {
      type,
      cx: -34 + rng() * 18,
      cy: -16 + rng() * 28,
      radius: 112 + rng() * 18,
      cutX: 14 + rng() * 38,
      cutRadius: 102 + rng() * 18,
      orbitPath: `M-180 ${round(16 + rng() * 24)} C-96 -142 116 -160 184 ${round(-26 + rng() * 32)} C108 124 -82 154 -174 ${round(46 + rng() * 18)}`,
      sparks
    };
  }

  if (type === "branching-tree") {
    const branches = [];
    const leaves = [];
    let branchId = 0;
    let leafId = 0;

    function grow(x, y, length, angle, depth, width) {
      const x2 = x + Math.cos(angle) * length;
      const y2 = y + Math.sin(angle) * length;
      const bend = (rng() - 0.5) * length * 0.44;
      branches.push({
        id: `branch-${branchId}`,
        d: `M${round(x)} ${round(y)} C${round((x + x2) / 2 + bend)} ${round((y + y2) / 2)} ${round((x + x2) / 2 - bend)} ${round((y + y2) / 2)} ${round(x2)} ${round(y2)}`,
        width,
        color: depth % 2 ? colors.ink : colors.accent
      });
      branchId += 1;

      if (depth <= 1) {
        leaves.push({
          id: `leaf-${leafId}`,
          x: round(x2),
          y: round(y2),
          rx: 8 + rng() * 12,
          ry: 14 + rng() * 18,
          rotation: (angle * 180) / Math.PI + 90,
          fill: rng() > 0.48 ? colors.accent2 : colors.accent,
          opacity: 0.76 + rng() * 0.2
        });
        leafId += 1;
        return;
      }

      const forkCount = rng() > 0.45 ? 3 : 2;
      for (let index = 0; index < forkCount; index += 1) {
        const spread = (index - (forkCount - 1) / 2) * (0.48 + rng() * 0.24);
        grow(x2, y2, length * (0.58 + rng() * 0.16), angle + spread, depth - 1, Math.max(2.2, width * 0.68));
      }
    }

    grow(0, 132, 110 + rng() * 22, -Math.PI / 2 + (rng() - 0.5) * 0.18, 4, 11);
    const roots = Array.from({ length: 7 }, (_, index) => {
      const angle = Math.PI * (0.08 + index / 8.5);
      const [x, y] = polarPoint(angle, 76 + rng() * 42);
      return {
        id: `root-${index}`,
        d: `M0 132 C${round(x * 0.32)} ${round(146 + rng() * 30)} ${round(x * 0.72)} ${round(166 + rng() * 34)} ${round(x)} ${round(132 + y * 0.52)}`,
        width: 4 + rng() * 4
      };
    });
    return { type, branches, leaves, roots };
  }

  if (type === "compass") {
    const spikeCount = rng() > 0.5 ? 8 : 12;
    const spikes = Array.from({ length: spikeCount }, (_, index) => {
      const angle = (Math.PI * 2 * index) / spikeCount - Math.PI / 2;
      const outer = index % 2 ? 118 + rng() * 28 : 178 + rng() * 34;
      const side = Math.PI / spikeCount * (0.36 + rng() * 0.22);
      const [tipX, tipY] = polarPoint(angle, outer);
      const [leftX, leftY] = polarPoint(angle - side, 30 + rng() * 12);
      const [rightX, rightY] = polarPoint(angle + side, 30 + rng() * 12);
      return {
        id: `spike-${index}`,
        d: `M${leftX} ${leftY} L${tipX} ${tipY} L${rightX} ${rightY} Z`,
        fill: index % 2 ? colors.accent2 : colors.accent,
        opacity: index % 2 ? 0.72 : 0.9
      };
    });
    return {
      type,
      spikes,
      outer: 118 + rng() * 18,
      inner: 54 + rng() * 20,
      crossPath: "M0 -176 V176 M-176 0 H176 M-124 -124 L124 124 M124 -124 L-124 124"
    };
  }

  if (type === "lattice") {
    const paths = Array.from({ length: 12 }, (_, index) => {
      const offset = -156 + index * 28;
      const horizontal = index % 2 === 0;
      return {
        id: `lat-${index}`,
        d: horizontal
          ? `M-170 ${round(offset)} C-80 ${round(offset - 44)} 80 ${round(offset + 44)} 170 ${round(offset)}`
          : `M${round(offset)} -170 C${round(offset + 44)} -72 ${round(offset - 44)} 72 ${round(offset)} 170`,
        stroke: index % 3 === 0 ? colors.accent : colors.accent2,
        width: 3 + rng() * 2.8,
        opacity: 0.54 + rng() * 0.2
      };
    });
    const cells = Array.from({ length: 10 }, (_, index) => {
      const angle = (Math.PI * 2 * index) / 10 + rng() * 0.08;
      const [cx, cy] = polarPoint(angle, 52 + (index % 2) * 62);
      const radius = 28 + rng() * 18;
      const points = Array.from({ length: 6 }, (_, pointIndex) => {
        const pointAngle = (Math.PI * 2 * pointIndex) / 6 + Math.PI / 6;
        const [x, y] = polarPoint(pointAngle, radius);
        return [round(cx + x), round(cy + y)];
      });
      return {
        id: `cell-${index}`,
        d: pathFromPoints(points, true),
        opacity: 0.62 + rng() * 0.22
      };
    });
    return { type, paths, cells };
  }

  if (type === "flame") {
    const flames = [0, 1, 2].map((layer) => {
      const height = 214 - layer * 52 + rng() * 22;
      const width = 116 - layer * 24 + rng() * 14;
      const sway = (rng() - 0.5) * 52;
      return {
        id: `flame-${layer}`,
        d: `M0 ${round(156 - layer * 16)} C-${round(width)} ${round(92 - layer * 8)} -${round(width * 0.66)} ${round(-24 - layer * 18)} ${round(sway)} -${round(height)} C${round(width * 0.96)} -${round(90 + layer * 20)} ${round(width * 1.08)} ${round(52 - layer * 12)} 0 ${round(156 - layer * 16)} Z`,
        fill: layer === 0 ? colors.accent : layer === 1 ? colors.accent2 : colors.glow,
        width: layer === 2 ? 3 : 5,
        opacity: layer === 0 ? 0.82 : 0.72
      };
    });
    const smoke = Array.from({ length: 8 }, (_, index) => {
      const x = -126 + index * 36 + rng() * 18;
      return {
        id: `smoke-${index}`,
        d: `M${round(x)} ${round(112 + rng() * 24)} C${round(x - 44)} ${round(48 - rng() * 20)} ${round(x + 50)} ${round(-32 + rng() * 28)} ${round(x + (rng() - 0.5) * 60)} ${round(-132 - rng() * 40)}`,
        stroke: index % 2 ? colors.accent2 : colors.ink,
        width: 2.4 + rng() * 2,
        opacity: 0.32 + rng() * 0.22
      };
    });
    return { type, flames, smoke };
  }

  const pointCount = rng() > 0.55 ? 11 : 9;
  const points = Array.from({ length: pointCount }, (_, index) => {
    const angle = (Math.PI * 2 * index) / pointCount - Math.PI / 2;
    const radius = index % 2 ? 72 + rng() * 48 : 154 + rng() * 48;
    return polarPoint(angle + rng() * 0.08, radius);
  });
  const innerPoints = Array.from({ length: 6 + Math.floor(rng() * 4) }, (_, index) => {
    const angle = (Math.PI * 2 * index) / (6 + Math.floor(rng() * 4)) + rng() * 0.2;
    const radius = 48 + rng() * 70;
    return polarPoint(angle, radius);
  });
  const segments = Array.from({ length: 9 }, (_, index) => {
    const a = chooseByRng(points, rng);
    const b = chooseByRng(points, rng);
    return {
      id: `n-${index}`,
      d: `M${a[0]} ${a[1]} L${b[0]} ${b[1]}`,
      stroke: index % 2 ? colors.accent2 : colors.ink,
      width: 2.8 + rng() * 2.4,
      opacity: 0.42 + rng() * 0.28
    };
  });
  return {
    type: "constellation",
    primaryPath: pathFromPoints(points, true),
    secondaryPath: pathFromPoints(innerPoints, false),
    segments
  };
}

function chooseByRng(items, rng) {
  return items[Math.floor(rng() * items.length)] || items[0];
}

function polarPoint(angle, radius) {
  return [round(Math.cos(angle) * radius), round(Math.sin(angle) * radius)];
}

function pathFromPoints(points, closed) {
  return points.map(([x, y], index) => `${index ? "L" : "M"}${round(x)} ${round(y)}`).join(" ") + (closed ? " Z" : "");
}

function downloadSvg(talisman) {
  const svg = document.getElementById("talisman-art");
  if (!svg) return;
  const content = `<?xml version="1.0" encoding="UTF-8"?>\n${svg.outerHTML}`;
  downloadBlob(content, `talisman-${talisman.id}.svg`, "image/svg+xml;charset=utf-8");
}

function downloadPng(talisman) {
  const svg = document.getElementById("talisman-art");
  if (!svg) return;
  const content = `<?xml version="1.0" encoding="UTF-8"?>\n${svg.outerHTML}`;
  const blob = new Blob([content], { type: "image/svg+xml;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const image = new Image();
  image.onload = () => {
    const canvas = document.createElement("canvas");
    canvas.width = 900;
    canvas.height = 1400;
    const context = canvas.getContext("2d");
    context.fillStyle = "#140f1f";
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(image, 0, 0);
    URL.revokeObjectURL(url);
    canvas.toBlob((pngBlob) => {
      if (pngBlob) {
        downloadBlob(pngBlob, `talisman-${talisman.id}.png`, "image/png");
      }
    }, "image/png");
  };
  image.src = url;
}

function downloadBlob(content, filename, type) {
  const blob = content instanceof Blob ? content : new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function shareUrlFor(talisman) {
  const url = new URL(window.location.origin);
  url.pathname = "/talisman";
  url.searchParams.set("t", encodeState(talisman));
  return url;
}

function hasTalismanToken() {
  return new URLSearchParams(window.location.search).has("t");
}

function readTalismanFromUrl() {
  const token = new URLSearchParams(window.location.search).get("t");
  if (!token) return null;
  try {
    const data = decodeState(token);
    if (!data.text || !data.intent || !data.palette || !data.style) {
      return null;
    }
    return createTalisman({
      text: String(data.text).slice(0, 260),
      intent: getIntent(data.intent).id,
      palette: getPalette(data.palette).id,
      style: getStyle(data.style).id,
      density: Number(data.density) || 64,
      seed: String(data.seed || "shared")
    });
  } catch {
    return null;
  }
}

function encodeState(value) {
  const data = JSON.stringify({
    text: value.text,
    intent: value.intent,
    palette: value.palette,
    style: value.style,
    density: value.density,
    seed: value.seed
  });
  const bytes = new TextEncoder().encode(data);
  let binary = "";
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

function decodeState(token) {
  const padded = token.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(token.length / 4) * 4, "=");
  const binary = atob(padded);
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  return JSON.parse(new TextDecoder().decode(bytes));
}

function saveRecent(talisman) {
  const items = getRecents();
  const next = [talisman, ...items.filter((item) => item.id !== talisman.id)].slice(0, 18);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
}

function getRecents() {
  try {
    const data = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
}

function getPalette(id) {
  return PALETTES.find((item) => item.id === id) || PALETTES[0];
}

function getIntent(id) {
  return INTENTS.find((item) => item.id === id) || INTENTS[0];
}

function getIntentDesign(id) {
  return INTENT_DESIGNS[id] || {
    motif: chooseByHash(id || "wish", MOTIF_TYPES),
    frames: FRAME_TYPES
  };
}

function getStyle(id) {
  return STYLES.find((item) => item.id === id) || STYLES[0];
}

function chooseByHash(value, items) {
  return items[hashString(value) % items.length] || items[0];
}

function hashString(value) {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

function mulberry32(seed) {
  return function random() {
    let t = seed += 0x6d2b79f5;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function wrapText(value, limit, maxLines) {
  const words = value.trim().split(/\s+/).filter(Boolean);
  const lines = [];
  let current = "";

  if (words.length <= 1 && value.length > limit) {
    for (let index = 0; index < value.length && lines.length < maxLines; index += limit) {
      lines.push(value.slice(index, index + limit));
    }
    return lines;
  }

  words.forEach((word) => {
    const next = current ? `${current} ${word}` : word;
    if (next.length > limit && current) {
      lines.push(current);
      current = word;
    } else {
      current = next;
    }
  });
  if (current) lines.push(current);
  return lines.slice(0, maxLines);
}

function round(value) {
  return Math.round(value * 10) / 10;
}

export default App;
